import { useState, useEffect } from 'react';
import { getAllUsersApi, updateUserRoleApi, deleteUserApi, createAdminApi } from '../../../api/apiServices';
import { useAuth } from '../../../context/useAuth';

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState('all');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  // Create Admin Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [adminForm, setAdminForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'admin',
    phoneNumber: '',
    bio: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createdAdminInfo, setCreatedAdminInfo] = useState(null);

  const currentUserRole = (currentUser?.role || '').toLowerCase();
  const isSuperAdmin = currentUserRole === 'superadmin';

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await getAllUsersApi(1, 100);
      if (res?.users) {
        setUsers(res.users);
      }
    } catch (err) {
      console.error("Error loading users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const generateRandomPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
    let pwd = "A1!";
    for (let i = 0; i < 9; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setAdminForm(prev => ({ ...prev, password: pwd }));
    setShowPassword(true);
  };

  const handleCreateAdminSubmit = async (e) => {
    e.preventDefault();
    setCreateError('');

    if (!adminForm.email || !adminForm.password) {
      setCreateError('Email and password are required.');
      return;
    }

    try {
      setCreateLoading(true);
      const res = await createAdminApi(adminForm);
      if (res?.status) {
        const newAdmin = res.user;
        setUsers(prev => [newAdmin, ...prev]);
        setCreatedAdminInfo({
          email: adminForm.email,
          password: adminForm.password,
          role: adminForm.role,
          name: `${adminForm.firstName} ${adminForm.lastName}`.trim() || adminForm.email,
        });
        setAdminForm({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          role: 'admin',
          phoneNumber: '',
          bio: '',
        });
      }
    } catch (err) {
      setCreateError(err?.response?.data?.message || err?.message || 'Failed to create admin user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole, userName) => {
    if (!window.confirm(`Are you sure you want to change ${userName || 'this user'}'s role to ${newRole.toUpperCase()}?`)) {
      return;
    }

    try {
      setActionLoading(userId);
      const res = await updateUserRoleApi(userId, newRole);
      if (res?.status) {
        setUsers(prev => prev.map(u => (u.userId === userId || u._id === userId) ? { ...u, role: newRole } : u));
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update role");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId, userName, targetRole) => {
    if (targetRole === 'superadmin' && !isSuperAdmin) {
      alert('Only a Super Administrator can remove another Super Administrator.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete user "${userName || 'this user'}"? All their data, enrollments, and progress will be permanently removed.`)) {
      return;
    }

    try {
      setActionLoading(userId);
      await deleteUserApi(userId);
      setUsers(prev => prev.filter(u => u.userId !== userId && u._id !== userId));
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete user");
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = users.filter(u => {
    const role = (u.role || '').toLowerCase();
    const matchesRole =
      filterRole === 'all' ||
      (filterRole === 'superadmin' && role === 'superadmin') ||
      (filterRole === 'admin' && (role === 'admin' || role === 'superadmin')) ||
      (filterRole === 'only_admin' && role === 'admin') ||
      (filterRole === 'instructor' && role === 'instructor') ||
      (filterRole === 'student' && role === 'student');

    const q = search.toLowerCase();
    const fullName = `${u.firstName || ''} ${u.lastName || ''} ${u.userName || ''}`.toLowerCase();
    const email = (u.email || '').toLowerCase();
    return matchesRole && (fullName.includes(q) || email.includes(q));
  });

  const superAdminCount = users.filter(u => (u.role || '').toLowerCase() === 'superadmin').length;
  const adminCount = users.filter(u => (u.role || '').toLowerCase() === 'admin').length;
  const instructorCount = users.filter(u => (u.role || '').toLowerCase() === 'instructor').length;
  const studentCount = users.filter(u => (u.role || '').toLowerCase() === 'student').length;

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-teal-950 rounded-3xl p-6 md:p-8 text-white shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-bold mb-3">
            <i className="ri-shield-user-line" /> Super Admin & Role Management Center
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-white">Users & Administrators Directory</h1>
          <p className="text-slate-300 text-sm max-w-xl mt-1 leading-relaxed">
            Create new administrators, manage role hierarchies, promote faculty, and monitor account privileges.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="px-4 py-2 rounded-2xl bg-white/10 backdrop-blur-xs border border-white/10 text-center">
            <span className="block text-xl font-extrabold text-amber-300">{superAdminCount + adminCount}</span>
            <span className="text-[10px] text-slate-300 uppercase tracking-wider font-semibold">Admins</span>
          </div>

          <button
            type="button"
            onClick={() => {
              setCreatedAdminInfo(null);
              setCreateError('');
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-extrabold text-xs transition-all shadow-lg hover:shadow-teal-500/20 cursor-pointer"
          >
            <i className="ri-user-add-fill text-base" />
            + Create Admin Account
          </button>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm space-y-6">
        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            <button
              type="button"
              onClick={() => setFilterRole('all')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                filterRole === 'all'
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
            >
              All Users ({users.length})
            </button>

            <button
              type="button"
              onClick={() => setFilterRole('superadmin')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                filterRole === 'superadmin'
                  ? 'bg-amber-500 text-slate-950 shadow-xs'
                  : 'bg-amber-50 hover:bg-amber-100 text-amber-800'
              }`}
            >
              👑 Super Admins ({superAdminCount})
            </button>

            <button
              type="button"
              onClick={() => setFilterRole('only_admin')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                filterRole === 'only_admin'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
            >
              Admins ({adminCount})
            </button>

            <button
              type="button"
              onClick={() => setFilterRole('instructor')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                filterRole === 'instructor'
                  ? 'bg-teal-600 text-white shadow-xs'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
            >
              Instructors ({instructorCount})
            </button>

            <button
              type="button"
              onClick={() => setFilterRole('student')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                filterRole === 'student'
                  ? 'bg-slate-700 text-white shadow-xs'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
            >
              Students ({studentCount})
            </button>
          </div>

          <div className="relative w-full sm:w-72">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
            />
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="py-20 text-center text-slate-400 text-xs space-y-2">
            <div className="w-8 h-8 rounded-full border-2 border-teal-500 border-t-transparent animate-spin mx-auto" />
            <p>Loading platform directory...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-14 text-center border border-dashed border-slate-200 rounded-2xl p-8">
            <i className="ri-user-search-line text-3xl text-slate-300 block mb-2" />
            <p className="text-sm font-bold text-slate-700">No users match the criteria</p>
            <p className="text-xs text-slate-400 mt-1">Try clearing filters or adjusting your search term.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
                  <th className="py-3 px-4">User Details</th>
                  <th className="py-3 px-4">Current Role</th>
                  <th className="py-3 px-4">Role Assignment</th>
                  <th className="py-3 px-4">Joined Date</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {filteredUsers.map((u) => {
                  const displayName = (u.firstName && u.lastName)
                    ? `${u.firstName} ${u.lastName}`
                    : u.userName || u.email.split('@')[0];
                  const role = (u.role || 'student').toLowerCase();
                  const isTargetSuperAdmin = role === 'superadmin';
                  const isCurrent = (currentUser?.userId === u.userId || currentUser?.email === u.email);

                  return (
                    <tr key={u.userId || u._id || u.email} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs ${
                            isTargetSuperAdmin
                              ? 'bg-amber-500 text-slate-950 shadow-xs'
                              : role === 'admin' 
                              ? 'bg-purple-600 text-white' 
                              : role === 'instructor' 
                              ? 'bg-teal-600 text-white' 
                              : 'bg-slate-600 text-white'
                          }`}>
                            {isTargetSuperAdmin ? '👑' : displayName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 flex items-center gap-1.5">
                              {displayName}
                              {isCurrent && (
                                <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-200 text-slate-700 font-semibold">You</span>
                              )}
                            </div>
                            <div className="text-slate-400 text-[11px] font-mono">{u.email}</div>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold capitalize ${
                          isTargetSuperAdmin
                            ? 'bg-amber-100 text-amber-900 border border-amber-300/60'
                            : role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : role === 'instructor'
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            isTargetSuperAdmin ? 'bg-amber-500' : role === 'admin' ? 'bg-purple-600' : role === 'instructor' ? 'bg-teal-600' : 'bg-slate-400'
                          }`} />
                          {isTargetSuperAdmin ? 'Super Admin' : role}
                        </span>
                      </td>

                      <td className="py-3.5 px-4">
                        <select
                          disabled={
                            actionLoading === (u.userId || u._id) ||
                            isCurrent ||
                            (isTargetSuperAdmin && !isSuperAdmin)
                          }
                          value={role}
                          onChange={(e) => handleRoleChange(u.userId || u._id, e.target.value, displayName)}
                          className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="student">Student</option>
                          <option value="instructor">Instructor</option>
                          <option value="admin">Administrator</option>
                          {isSuperAdmin && (
                            <option value="superadmin">👑 Super Administrator</option>
                          )}
                        </select>
                      </td>

                      <td className="py-3.5 px-4 text-slate-500 text-[11px]">
                        {new Date(u.createdAt || Date.now()).toLocaleDateString()}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        {!isCurrent && (!isTargetSuperAdmin || isSuperAdmin) && (
                          <button
                            type="button"
                            onClick={() => handleDeleteUser(u.userId || u._id, displayName, role)}
                            disabled={actionLoading === (u.userId || u._id)}
                            className="text-slate-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer"
                            title="Delete User"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-2xl border border-slate-100 relative">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="absolute top-5 right-5 text-slate-400 hover:text-slate-700 w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>

            <div className="mb-5">
              <div className="w-11 h-11 rounded-2xl bg-teal-500/10 text-teal-600 flex items-center justify-center mb-3">
                <i className="ri-shield-keyhole-fill text-xl" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Create New Administrator</h2>
              <p className="text-xs text-slate-500 mt-1">
                Provision administrative credentials directly. The user can sign in immediately at /login.
              </p>
            </div>

            {createdAdminInfo ? (
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs space-y-2">
                  <div className="font-bold flex items-center gap-1.5 text-sm">
                    <i className="ri-checkbox-circle-fill text-emerald-600 text-base" />
                    Admin Account Ready!
                  </div>
                  <p>Here are the login credentials for <strong>{createdAdminInfo.name}</strong>:</p>
                  <div className="bg-white p-3 rounded-xl font-mono text-slate-800 space-y-1 border border-emerald-100 text-[11px]">
                    <div><strong>Email:</strong> {createdAdminInfo.email}</div>
                    <div><strong>Password:</strong> {createdAdminInfo.password}</div>
                    <div><strong>Role:</strong> {createdAdminInfo.role === 'superadmin' ? 'Super Administrator' : 'Administrator'}</div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`Email: ${createdAdminInfo.email}\nPassword: ${createdAdminInfo.password}`);
                      alert('Credentials copied to clipboard!');
                    }}
                    className="flex-1 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition-all cursor-pointer"
                  >
                    <i className="ri-file-copy-line mr-1" /> Copy Credentials
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreatedAdminInfo(null)}
                    className="py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-all cursor-pointer"
                  >
                    Create Another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleCreateAdminSubmit} className="space-y-4">
                {createError && (
                  <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                    {createError}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">First Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Alex"
                      value={adminForm.firstName}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Morgan"
                      value={adminForm.lastName}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    placeholder="admin@learnhub.com"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] font-bold text-slate-700">Password *</label>
                    <button
                      type="button"
                      onClick={generateRandomPassword}
                      className="text-[10px] text-teal-600 hover:text-teal-700 font-bold cursor-pointer"
                    >
                      ⚡ Generate Secure Password
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      placeholder="Minimum 6 characters"
                      value={adminForm.password}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 font-mono focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                    >
                      <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">Admin Level</label>
                  <select
                    value={adminForm.role}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  >
                    <option value="admin">Administrator (Platform & Course Management)</option>
                    {isSuperAdmin && (
                      <option value="superadmin">👑 Super Administrator (Full Root Authority)</option>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">Phone Number (Optional)</label>
                  <input
                    type="text"
                    placeholder="+1 (555) 000-0000"
                    value={adminForm.phoneNumber}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  />
                </div>

                <div className="pt-2 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-950 bg-teal-400 hover:bg-teal-300 transition-all shadow-md cursor-pointer disabled:opacity-50"
                  >
                    {createLoading ? 'Creating Account...' : 'Create Admin User'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
